// ─────────────────────────────────────────────────────────────────────────────
// Account / band-switcher data layer. Powers the top-nav band switcher: the
// band avatar photo, the list of bands the user belongs to, switching the active
// band, and logout. Mirrors profile-wiring.js (toggleBandSwitcher / switchToBand)
// and contacts-wiring.js (photoUrlForUser).
// ─────────────────────────────────────────────────────────────────────────────

import { STATS_WORKER, apiGet, apiPost } from './api';
import { clearToken, ACTIVE_BAND_KEY } from './auth';
import { clearDataCache } from './dataCache';
import type { User } from './types';

/** One band the signed-in user is a member of (GET /api/user/bands → bands[]). */
export interface UserBand {
  readonly band_name: string;
  readonly role?: string;
  readonly is_primary?: boolean;
}

interface UserBandsResponse {
  readonly bands?: ReadonlyArray<UserBand>;
}

/**
 * GET /api/user/bands → the bands the user can switch between.
 * Mirrors profile-wiring.js fetchUserBands (:1508-1512):
 *   fetch(AUTH_WORKER + '/api/user/bands', { headers: authHeaders(false) })
 *     .then(r => r.ok ? r.json() : null).then(res => (res && res.bands) || []);
 */
export async function fetchUserBands(): Promise<UserBand[]> {
  const res = await apiGet<UserBandsResponse>('auth', '/api/user/bands').catch(() => null);
  return res && Array.isArray(res.bands) ? res.bands.slice() : [];
}

/**
 * The band's avatar (photo1) URL, the stats worker's cached binary photo
 * endpoint, keyed by the band's founder user id. Mirrors contacts-wiring.js
 * photoUrlForUser (:29-32): STATS_WORKER + '/photo?u=<uid>&which=photo1'.
 * Returns '' when there's no id (→ the switcher keeps its initials fallback).
 */
export function bandPhotoUrl(user: Pick<User, 'id' | 'band_user_id'> | null): string {
  if (!user) return '';
  const uid = user.band_user_id ?? user.id;
  if (uid == null || uid === '') return '';
  return STATS_WORKER + '/photo?u=' + encodeURIComponent(String(uid)) + '&which=photo1';
}

/**
 * POST /api/user/switch-band { band_name } then full-reload so every surface
 * re-fetches against the new band's data tree. Mirrors profile-wiring.js
 * switchToBand (:1592-1614). A different band is a different data tree.
 */
export async function switchBand(bandName: string): Promise<void> {
  await apiPost<{ ok?: boolean }>('auth', '/api/user/switch-band', { band_name: bandName });
  try {
    window.location.reload();
  } catch {
    /* noop, SSR / no window */
  }
}

/** Clear the auth token and return to the login surface. Awaits clearToken() so
 *  the native Keychain copy + logout marker are cleared too (not just
 *  localStorage), otherwise a native-wrapped /app-react would re-auth from the
 *  Keychain token on next boot. */
export async function logout(): Promise<void> {
  try {
    await clearToken();
  } catch {
    /* noop, fall through to navigation regardless */
  }
  // Scorch per-band / per-role client state so it can't carry into the next
  // login on a shared browser: the active-band pin (→ X-Band-Id), the mobile
  // role cache, and the band-keyed data cache. (audit 2026-07-06 P1-c.)
  try {
    localStorage.removeItem(ACTIVE_BAND_KEY);
    localStorage.removeItem('ac:last_role');
  } catch {
    /* no storage */
  }
  try {
    clearDataCache();
  } catch {
    /* noop */
  }
  try {
    // Stay inside the React app on logout. Going to '/' lands on the marketing
    // home, whose sign-in is the LEGACY login → it sends you to /app.html, so a
    // React user gets bounced out of React entirely. Reload the current
    // /app path instead: with no token App.tsx renders the React login, and
    // signing back in flips in place, re-login never leaves React. (Preserving
    // the path keeps a roster user on /app/roster through the round-trip.)
    const path = window.location.pathname.startsWith('/app')
      ? window.location.pathname
      : '/app';
    window.location.href = path;
  } catch {
    /* noop */
  }
}
